import { FranchiseData, BlogPost, Package } from '../config/api'
import { getPackageDestination } from './packageHelpers'
import { getBrandLogoUrl } from '../components/BrandMark'

export const buildOrganizationSchema = (brand: FranchiseData) => ({
  '@context': 'https://schema.org',
  '@type': 'Organization',
  name: brand.name,
  url: `https://${brand.subdomain}.agencycms.com`,
  logo: getBrandLogoUrl(),
  sameAs: [brand.facebook, brand.instagram, brand.linkedin].filter(Boolean),
  contactPoint: [
    {
      '@type': 'ContactPoint',
      telephone: brand.telephone,
      contactType: 'customer service',
      areaServed: 'ZA',
      availableLanguage: ['en'],
    },
  ],
  address: brand.address
    ? {
        '@type': 'PostalAddress',
        streetAddress: brand.address,
        addressCountry: 'ZA',
      }
    : undefined,
})

export const buildWebsiteSchema = (brand: FranchiseData) => ({
  '@context': 'https://schema.org',
  '@type': 'WebSite',
  name: brand.name,
  url: `https://${brand.subdomain}.agencycms.com`,
  potentialAction: {
    '@type': 'SearchAction',
    target: `https://${brand.subdomain}.agencycms.com/search?q={search_term_string}`,
    'query-input': 'required name=search_term_string',
  },
})

export const buildWebPageSchema = (params: {
  name: string
  description?: string
  url: string
  breadcrumbItems?: Array<{ name: string; url: string }>
}) => {
  const webPage: Record<string, any> = {
    '@context': 'https://schema.org',
    '@type': 'WebPage',
    name: params.name,
    description: params.description,
    url: params.url,
  }
  if (params.breadcrumbItems && params.breadcrumbItems.length) {
    webPage.breadcrumb = buildBreadcrumbSchema(params.breadcrumbItems)
  }
  return webPage
}

export const buildBreadcrumbSchema = (
  items: Array<{ name: string; url: string }>
) => ({
  '@context': 'https://schema.org',
  '@type': 'BreadcrumbList',
  itemListElement: items.map((item, index) => ({
    '@type': 'ListItem',
    position: index + 1,
    name: item.name,
    item: item.url,
  })),
})

export const buildBlogPostingSchema = (post: BlogPost, url: string) => ({
  '@context': 'https://schema.org',
  '@type': 'BlogPosting',
  headline: post.title,
  description: post.excerpt || post.description?.slice(0, 160),
  datePublished: post.created_at,
  dateModified: post.updated_at || post.created_at,
  author: post.author_name
    ? { '@type': 'Person', name: post.author_name }
    : undefined,
  image: post.featured_image?.url,
  mainEntityOfPage: url,
})

export const buildProductSchema = (pkg: Package, url: string) => ({
  '@context': 'https://schema.org',
  '@type': 'Product',
  name: pkg.name,
  description: pkg.intro || pkg.description?.replace(/<[^>]*>/g, '').slice(0, 200),
  image:
    pkg.cover_photo?.large_url ||
    pkg.cover_photo?.url ||
    pkg.scraped_cover_photo ||
    undefined,
  brand: getPackageDestination(pkg),
  url,
  offers: {
    '@type': 'Offer',
    priceCurrency: pkg.price_currency || 'ZAR',
    price: (pkg.price_cents || 0) / 100,
    availability: 'https://schema.org/InStock',
    url,
    validFrom: pkg.valid_from,
    validThrough: pkg.valid_to,
  },
})

export const buildCollectionPageSchema = (params: {
  name: string
  description?: string
  url: string
}) => ({
  '@context': 'https://schema.org',
  '@type': 'CollectionPage',
  name: params.name,
  description: params.description,
  url: params.url,
})


